import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import Layout from "../../../../layouts/Layout";
import { useDispatch, useSelector } from "react-redux";
import { getSearchOptions, getTournamentBaselineConfigAPI, updateTournamentBaselineConfigAPI } from "../../../../services/api";
import * as PR from "../../../../prime-react/index";
import "../anomalies/Anomalies.scss";
import { currencyFormat, DateFormat, formatTime, pagecount, normalizeText, getCurrencySymbolByGroupId } from "../../../../utils/reuse";
import "./BaselineConfiguration.scss";
import { useToast } from "../../../../context/ToastContext";
import SearchAndTotalSummary from "../search-total-summary/SearchAndTotalSummary";
import { filterOptionsActions } from "../../../../store/FiltersOptions";

const BaselineConfiguration = () => {
    const dispatch = useDispatch();
    const { showToast } = useToast();
    const abortControllerRef = useRef(null);
    const groupLoadedRef = useRef(false);
    const sportLoadedRef = useRef(false);
    const [loading, setLoading] = useState(false);
    const sessionData = useSelector((state) => state.auth.sessionData);
    const [page, setPage] = useState(1);
    const [first, setFirst] = useState(0);
    const [sortField, setSortField] = useState('computedAt');
    const [sortOrder, setSortOrder] = useState(-1);
    const headers = useMemo(() => {
        return { sessionid: sessionData.sessionId };
    }, [sessionData.sessionId]);
    const [searchOptions, setSearchOptions] = useState({
        group: [],
        location: [],
        sport: []
    });
    const alertPlatformState = useSelector(state => state.alertPlatformFilter.baselineFilters);
    const [searchRequest, setSearchRequest] = useState({ ...alertPlatformState, type: 'sport' });
    const [defaultGroup, setDefaultGroup] = useState('');
    const [defaultState, setDefaultState] = useState('');

    const [tournamentList, setTournamentList] = useState([]);
    const [allTournamentList, setAllTournamentList] = useState([]);
    const [editRowId, setEditRowId] = useState(null);
    const [tempBaselineValue, setTempBaselineValue] = useState(null);
    const storedFilterOptions = useSelector((state) => state.filterOptions);
    
    const searchRequestHandler = (data) => {
        setSearchRequest(data);
    };
    const filterHandler = (value, type) => {
        const data = searchOptions[type]?.length ? searchOptions[type] : [];
        const find = data.find(d => d.id === value);
        if (type === 'group') {
            setDefaultGroup(find)
            setDefaultState({})
        }
    }
    const getSearchOptionsHandler = useCallback((type, groupParam = '') => {
        const getResponse = (type) => (response) => {
            if (response.result === "SUCCESS") {
                let filterOptions = response.data?.length ? response.data.map(r => ({ id: r.id, name: r.name })).filter(r => r.name?.toLowerCase() !== 'unknown') : [];
                if (type === 'sport') {
                    filterOptions = response.data?.length
                        ? response.data.map((r) => ({ id: r.id, name: r.name })).sort((a, b) => a.name.localeCompare(b.name)).filter(r => r.name?.toLowerCase() !== 'unknown')
                        : [];
                    dispatch(filterOptionsActions.storeFilterOptions({ key: type, options: filterOptions }))
                }
                if (type === 'group' && filterOptions?.length) {
                    setDefaultGroup(filterOptions[0])
                }
                if (type === 'location' && filterOptions?.length) {
                    setDefaultState(filterOptions[0])
                }
                setSearchOptions((prev) => ({
                    ...prev,
                    [type]: [...filterOptions]
                }));
            } else {
                setSearchOptions((prev) => ({
                    ...prev,
                    [type]: []
                }));
            }
        };
        getSearchOptions(type, headers, dispatch, getResponse(type), groupParam);
    }, [dispatch, headers]);

    useEffect(() => {
        if (defaultGroup?.id) {
            getSearchOptionsHandler('location', `groups[]=${defaultGroup?.id}`);
        } 
    }, [getSearchOptionsHandler, defaultGroup]);
    useEffect(() => {
        if (groupLoadedRef.current) return;
        
        if (storedFilterOptions['group']?.length === 0) {
            getSearchOptionsHandler('group');
        } else {
            setDefaultGroup(storedFilterOptions['group'][0])
            setSearchOptions((prev) => ({
                ...prev,
                'group': [...storedFilterOptions['group']]
            }));
        }
        groupLoadedRef.current = true;
    }, [getSearchOptionsHandler, storedFilterOptions]);
    useEffect(() => {
        if (sportLoadedRef.current) return;

        if (storedFilterOptions['sport']?.length === 0) {
            getSearchOptionsHandler('sport');
        } else {
            setSearchOptions((prev) => ({
                ...prev,
                'sport': [...storedFilterOptions['sport']]
            }));
        }
        sportLoadedRef.current = true;
    }, [getSearchOptionsHandler, storedFilterOptions]);
    const buildPayload = useCallback((request) => {
        const data = {};
        data.groupID = request.groupID ? request.groupID : defaultGroup?.id;
        data.stateID = request.stateID ? request.stateID : defaultState?.id;
        if (request.baselineSports?.length) {
            data.sportIDs = request.baselineSports.map(s => s.id);
        }
        return data;
    }, [defaultGroup?.id, defaultState?.id]);

    const getTournamentsHandler = useCallback((data, sports, tournamentKeyword, type = '') => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        const { signal } = abortControllerRef.current;

        onEditCancel();
        setPage(1);
        // Create a mapping of sportID to sport name
        const sportMap = {};
        sports.forEach(sport => {
            sportMap[sport.id] = sport.name;
        });
        setLoading(true);
        getTournamentBaselineConfigAPI(data, headers, dispatch, (response) => {
            if (signal.aborted) return;
            setLoading(false);
            if (response.result === "SUCCESS") {
                const responseData = response.data?.map(r => ({
                    ...r,
                    sportName: sportMap[r.sportID] || 'Unknown',
                    assignedBaseline: r.assignedBaseline !== undefined ? r.assignedBaseline : null

                })).sort((a, b) => a.name.localeCompare(b.name)).filter(r => r.name?.toLowerCase() !== 'unknown') || [];
                let options = [...responseData];
                if (tournamentKeyword) {
                    const keywordNorm = tournamentKeyword.toLowerCase();
                    options = options.filter(option => normalizeText(option.name).includes(keywordNorm));
                }
                setTournamentList(options);
                setAllTournamentList([...responseData]);
            } else {
                setTournamentList([]);
                setAllTournamentList([]);
                showToast("warn", "Failed to fetch tournaments. Please try again.");
            }
        }, signal);
    }, [dispatch, headers, showToast]);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);
    useEffect(() => {
        if (defaultGroup?.id && defaultState?.id && searchRequest.type !== 'tournament') {
            if (searchRequest.baselineSports?.length > 0) {
                const data = buildPayload(searchRequest);
                getTournamentsHandler(data, searchRequest.baselineSports, searchRequest.tournamentKeywordSearch);

            } else {
                setTournamentList([]);
                setAllTournamentList([]);
                setEditRowId(null);
                setTempBaselineValue(null);
                setPage(1);
            }
        }
    }, [searchRequest.type, searchRequest.baselineSports, searchRequest.tournamentKeywordSearch, buildPayload, defaultGroup?.id, defaultState?.id, searchRequest, getTournamentsHandler]);
    useEffect(() => {
        if (searchRequest.type === 'tournament') {
            setEditRowId(null);
            setTempBaselineValue(null);
            setPage(1);
            const keywordNorm = searchRequest.tournamentKeywordSearch?.toLowerCase() || '';
            const filteredTournaments = allTournamentList.filter(option => normalizeText(option.name).includes(keywordNorm));
            setTournamentList(filteredTournaments);
        }
    }, [allTournamentList, searchRequest.tournamentKeywordSearch, searchRequest.type]);
    const assignedDateDisplay = (rowData) => {
        return rowData.assignedAt ? (
            <div className="flex align-items-center gap-3">
                <div className="event-column">
                    <p>{DateFormat(rowData.assignedAt)}</p>
                    <span>{formatTime(rowData.assignedAt)}</span>
                </div>
            </div>
        ) : '-';
    };

    const computedDisplay = (rowData) => {
        return rowData.computedAt ? (
            <div className="flex align-items-center gap-3">
                <div className="event-column">
                    <p>{DateFormat(rowData.computedAt)}</p>
                    <span>{formatTime(rowData.computedAt)}</span>
                </div>
            </div>
        ) : '-';
    };
    const computedBaselineDisplay = (rowData) => {
        return rowData.computedBaseline !== undefined ? currencyFormat(rowData.computedBaseline, defaultGroup?.id) : '-';
    };

    const onEditInit = (rowData) => {
        setEditRowId(rowData.id);
        setTempBaselineValue(rowData.assignedBaseline);
    };

    const onEditCancel = () => {
        setEditRowId(null);
        setTempBaselineValue(null);
    };

    const onEditSave = async (rowData, baselineValue = tempBaselineValue) => {
        if (rowData.assignedBaseline === baselineValue){
            showToast("warn", "No changes were made");
            return;
        }
        const isNullBaseline = baselineValue === null || baselineValue === undefined;
        const numValue = isNullBaseline ? null : parseFloat(baselineValue);

        if (!isNullBaseline && (isNaN(numValue) || numValue <= 0)) {
            showToast("warn", "Please enter a number greater than 0");
            return;
        }

        setLoading(true);
        try {
            const payload = [
                {
                    tournamentID: rowData.id,
                    assignedBaseline: isNullBaseline ? null : numValue
                }
            ];
            updateTournamentBaselineConfigAPI(payload, headers, dispatch, (response) => {
                if (response.result === "SUCCESS") {
                    // Update the tournament in the list with new baseline and assignedAt timestamp
                    const updatedBaseline = isNullBaseline ? null : numValue;
                    const updatedTime = new Date().toISOString();

                    const updateTournament = (t) =>
                        t.id === rowData.id
                            ? { ...t, assignedBaseline: updatedBaseline, assignedAt: updatedTime }
                            : t;

                    setTournamentList(prev => prev.map(updateTournament));
                    setAllTournamentList(prev => prev.map(updateTournament));
                    setEditRowId(null);
                    setTempBaselineValue(null);
                    showToast("success", "Baseline config has been successfully updated");
                } else {
                    showToast("warn", "Failed to update baseline config. Please try again.");
                    onEditCancel()
                }
            });
        } catch (error) {
            console.error('Error updating baseline:', error);
        } finally {
            setLoading(false);
        }
    };

    const assignedBaselineBody = (rowData) => {
        if (editRowId === rowData.id) {
            return (
                <div className="assigned-baseline-container">
                    <PR.InputNumber
                        value={tempBaselineValue}
                        onValueChange={(e) => setTempBaselineValue(e.value)}
                        mode="decimal"
                        prefix={getCurrencySymbolByGroupId(defaultGroup?.id)}
                        minFractionDigits={2}
                        maxFractionDigits={2}
                        className="w-full assigned-baseline-input"
                    />
                    {
                        tempBaselineValue !== null && tempBaselineValue !== undefined &&
                        <i className="pi pi-times" onClick={() => setTempBaselineValue(null)}></i>
                    }
                   
                </div>
            );
        }
        return rowData.assignedBaseline !== null && rowData.assignedBaseline !== undefined ? currencyFormat(rowData.assignedBaseline, defaultGroup?.id) : '-';
    };

    const actionBody = (rowData) => {

        if (editRowId === rowData.id) {
            return (
                <div style={{ display: 'flex', gap: '5px' }}>
                    <PR.Button
                        label="Update"
                        className="view-button width-auto"
                        icon="pi pi-check"
                        onClick={() => onEditSave(rowData)}
                    />
                    <PR.Button
                        label="Cancel"
                        icon="pi pi-times"
                        className="view-button width-auto cancel-button"
                        onClick={onEditCancel}
                    />
                </div>
            );
        }
        return (
            <PR.Button
                label="Edit"
                className="view-button width-auto"
                icon="pi pi-pencil"
                onClick={() => onEditInit(rowData)}
                disabled={editRowId !== null && editRowId !== rowData.id}
            />
        );
    };
    const handlePageChange = (currentPage) => {
        setPage(currentPage);
        setFirst((currentPage - 1) * pagecount);
    };

    const onPage = (event) => {
        // event.first is the index of the first record
        setFirst(event.first);
        setPage(Math.floor(event.first / pagecount) + 1);
    };

    const onSort = (event) => {
        setSortField(event.sortField);
        setSortOrder(event.sortOrder);
        // reset to first page on sort
        setPage(1);
        setFirst(0);
    };

    const getPaginatedData = () => {
        // Return a sorted copy of the full list so sorting applies to all records
        // without mutating the original state array. Sorting uses the controlled
        // `sortField` and `sortOrder` so DataTable pagination and sorting stay in sync.
        const key = sortField || 'assignedAt';
        const order = sortOrder || -1;
        // if sorting by a date field, compare as dates, otherwise do basic compare
        return [...tournamentList].sort((a, b) => {
            const valA = a[key];
            const valB = b[key];
            // handle dates
            if (key.toLowerCase().includes('at') || !isNaN(Date.parse(valA)) || !isNaN(Date.parse(valB))) {
                const dateA = valA ? new Date(valA).getTime() : 0;
                const dateB = valB ? new Date(valB).getTime() : 0;
                return order === -1 ? dateB - dateA : dateA - dateB;
            }
            // numeric compare if both are numbers
            if (typeof valA === 'number' && typeof valB === 'number') {
                return order === -1 ? valB - valA : valA - valB;
            }
            // fallback to string compare
            const aStr = (valA || '').toString().toLowerCase();
            const bStr = (valB || '').toString().toLowerCase();
            if (aStr < bStr) return order === -1 ? 1 : -1;
            if (aStr > bStr) return order === -1 ? -1 : 1;
            return 0;
        });
    };

    const getTotalPages = () => {
        return Math.ceil(tournamentList.length / pagecount);
    };

    const paginationTemplate = {
        layout: 'CurrentPageReport PrevPageLink NextPageLink',
        CurrentPageReport: (options) => {
            const totalPages = getTotalPages();
            return (<span className='current-page'>Page {page} of {totalPages}</span>)
        },
        PrevPageLink: (options) => {
            return (<PR.Button className='pagination-buttons'
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1 || editRowId}>Previous</PR.Button>
            )
        },
        NextPageLink: (options) => {
            const totalPages = getTotalPages();
            return (<PR.Button disabled={page >= totalPages || editRowId} onClick={() => handlePageChange(page + 1)}
                className='pagination-buttons'>Next</PR.Button>)
        }
    };
    return (
        <>
            <section className="analyst-home-section statistics-betting-section ">
                <SearchAndTotalSummary searchRequestHandler={searchRequestHandler} filterHandler={filterHandler} totalData={{}} searchOptions={searchOptions} loading={false} alertPlatformState={alertPlatformState} defaultGroup={defaultGroup} defaultState={defaultState} screenType={'baseline'} />
                <Layout>
                    <section className='anomalies-history-section baseline-configuration-section'>
                        <div className="displayGrid">
                            <div className="m-0">
                                <h1>Baseline Configuration</h1>
                            </div>
                        </div>

                        <PR.DataTable
                            value={getPaginatedData()}
                            loading={loading}
                            stripedRows
                            className="data-table"
                            sortField={sortField}
                            sortOrder={sortOrder}
                            sortMode="single"
                            onSort={onSort}
                            paginator
                            paginatorTemplate={paginationTemplate}
                            rows={pagecount}
                            first={first}
                            onPage={onPage}
                            dataKey="id"
                        >
                            <PR.Column field="name" header="Tournament Name" sortable />
                            <PR.Column field="sportName" header="Sport Name" sortable />
                            <PR.Column body={computedBaselineDisplay} field="computedBaseline" header="Computed Baseline" className="baseline-column" sortable />
                            <PR.Column body={computedDisplay} field="computedAt" header="Computed At" sortable className="date-column" />
                            <PR.Column body={assignedBaselineBody} field="assignedBaseline" header="Assigned Baseline" className="baseline-column" sortable />
                            <PR.Column body={assignedDateDisplay} field="assignedAt" header="Updated At" className="date-column" sortable />
                            <PR.Column body={actionBody} className="action-column" header="Action"></PR.Column>
                        </PR.DataTable>
                    </section>

                </Layout>


            </section>
        </>
    );
}

export default BaselineConfiguration;
